import { getFunctions, httpsCallable } from "firebase/functions";
import { auth, app } from "../config/firebase";
import { UserRole } from "../types/UserRole";
import { BananaStats } from "../config/Types";
import { RelayIntent } from "../relay/RelayPrompts";
import { MyRelayJourney, RelayMetrics, RelayPassport } from "../relay/RelayTypes";

export class BananaApi {

  public static createBanana = async (type: string, isPublic: boolean, senderName: string, recipientName: string, message: string) => {
    if (!auth.currentUser) throw new Error("Not signed in");
    const apiCall = httpsCallable<{ type: string, isPublic: boolean, senderName: string, recipientName: string, message: string }, { banana: any; bananaOperation: any; user: any }>(
      getFunctions(app),
      "createRelayRoot"
    );
    try {
      const res = await apiCall({ type, isPublic, senderName, recipientName, message });
      return res.data;
    } catch (error: any) {
      throw new Error(error.message || "Failed to create a banana.");
    }
  }

  public static forwardBanana = async (privateId: string, publicId: string, password: string, senderName: string, recipientName: string, message: string) => {
    const apiCall = httpsCallable<{ privateId: string, publicId: string, password: string, senderName: string, recipientName: string, message: string }, { banana: any; bananaOperation: any; user: any }>(
      getFunctions(app),
      "sendBananaForward"
    );
    try {
      const res = await apiCall({ privateId, publicId, password, senderName, recipientName, message });
      return res.data;
    } catch (error: any) {
      throw new Error(error.message || "Failed to forward a banana.");
    }
  }

  public static bananaBack = async (privateId: string, publicId: string, password: string, senderName: string, recipientName: string, message: string) => {
    const apiCall = httpsCallable<{ privateId: string, publicId: string, password: string, senderName: string, recipientName: string, message: string }, { banana: any; bananaOperation: any; user: any }>(
      getFunctions(app),
      "sendBananaBack"
    );
    try {
      const res = await apiCall({ privateId, publicId, password, senderName, recipientName, message });
      return res.data;
    } catch (error: any) {
      throw new Error(error.message || "Failed to banana back.");
    }
  }

  public static createRelayChild = async (privateId: string, publicId: string, password: string, senderName: string, recipientName: string, message: string, intent: RelayIntent) => {
    const apiCall = httpsCallable<
      { privateId: string; publicId: string; password: string; senderName: string; recipientName: string; message: string; intent: RelayIntent },
      { banana: any; bananaOperation: any; user: any; passport: RelayPassport }
    >(getFunctions(app), "createRelayChild");
    try {
      const res = await apiCall({ privateId, publicId, password, senderName, recipientName, message, intent });
      return res.data;
    } catch (error: any) {
      throw new Error(error.message || "Failed to keep this banana moving.");
    }
  }

  public static reactToBanana = async (privateId: string, publicId: string, password: string, reaction: "heart" | "smile" | "banana") => {
    const apiCall = httpsCallable<
      { privateId: string; publicId: string; password: string; reaction: string },
      { success: boolean; reaction: string; alreadyReacted?: boolean; changed?: boolean }
    >(getFunctions(app), "reactToBanana");
    const res = await apiCall({ privateId, publicId, password, reaction });
    return res.data;
  }

  public static getRelayPassport = async (privateId: string, publicId: string, password: string): Promise<RelayPassport> => {
    const apiCall = httpsCallable<{ privateId: string; publicId: string; password: string }, RelayPassport>(
      getFunctions(app),
      "getRelayPassport"
    );
    const res = await apiCall({ privateId, publicId, password });
    return res.data;
  }

  public static getMyRelayJourneys = async (): Promise<MyRelayJourney[]> => {
    if (!auth.currentUser) throw new Error("Not signed in");
    const apiCall = httpsCallable<{}, { journeys: MyRelayJourney[] }>(
      getFunctions(app),
      "getMyRelayJourneys"
    );
    const res = await apiCall({});
    return res.data.journeys || [];
  }

  public static getRelayMetrics = async (days: 0 | 1 | 7 | 30): Promise<RelayMetrics> => {
    if (!auth.currentUser) throw new Error("Not signed in");
    const apiCall = httpsCallable<{ days: number }, RelayMetrics>(
      getFunctions(app),
      "getRelayMetrics"
    );
    const res = await apiCall({ days });
    return res.data;
  }
      
  public static unwrapBanana = async (privateId: string, publicId: string, password: string) => {
    const apiCall = httpsCallable<{ privateId: string, publicId: string, password: string }, { isWrapped: boolean; bananaOperation: any, canBananaBack: boolean }>(
      getFunctions(app),
      "unwrapBanana"
    );
    try {
      const res = await apiCall({ privateId, publicId, password });
      return res.data;
    } catch (error: any) {
      throw new Error(error.message || "Failed to unwrap the banana.");
    }
  }

  public static getPublicBananaTransactions = async () => {
    const apiCall = httpsCallable<{}, {
      bananaOperations: any[];
      publicImpact: { sharedMessages: number; countries: number; internationalRoutes: number };
    }>(
      getFunctions(app),
      "getPublicBananaTransactions"
    );
    try {
      const res = await apiCall({});
      return res?.data ?? {
        bananaOperations: [],
        publicImpact: { sharedMessages: 0, countries: 0, internationalRoutes: 0 },
      };
    } catch (error: any) {
      throw new Error(error.message || "Failed to get public banana transactions.");
    }
  }

  public static publishJoyPost = async (bananaId: string, message: string) => {
    const apiCall = httpsCallable<{ bananaId: string; message: string }, { published: boolean }>(
      getFunctions(app),
      "publishJoyPost"
    );
    const res = await apiCall({ bananaId, message });
    return res.data;
  }

  public static unpublishJoyPost = async (bananaId: string) => {
    const apiCall = httpsCallable<{ bananaId: string }, { published: boolean }>(
      getFunctions(app),
      "unpublishJoyPost"
    );
    const res = await apiCall({ bananaId });
    return res.data;
  }

  public static getMyJoyPost = async (bananaId: string) => {
    const apiCall = httpsCallable<{ bananaId: string }, { published: boolean; message: string }>(
      getFunctions(app),
      "getMyJoyPost"
    );
    const res = await apiCall({ bananaId });
    return res.data;
  }

  public static getBananaverseStats = async (): Promise<BananaStats> => {
    const apiCall = httpsCallable<{}, BananaStats>(
      getFunctions(app),
      "getBananaverseStats"
    );
    try {
      const res = await apiCall({});
      return res?.data ?? {
        totalSent: 0,
        totalForwarded: 0,
        totalSentBack: 0,
      };
    } catch (error: any) {
      throw new Error(error.message || "Failed to get bananaverse stats.");
    }
  }

  public static getBananaTransactions = async () => {
    if (!auth.currentUser) throw new Error("Not signed in");
    const apiCall = httpsCallable<{}, { bananaOperations: any[] }>(
      getFunctions(app),
      "getBananaTransactions"
    );
    try {
      const res = await apiCall({});
      return res?.data?.bananaOperations ?? [];
    } catch (error: any) {
      throw new Error(error.message || "Failed to get banana transactions.");
    }
  }

  public static getBananaByPublicId = async (publicId: string, password?: string | null): Promise<{ exists: boolean; error: string | null; banana: any; bananaOperations: any[]; isWrapped: boolean; role: UserRole; canBananaBack: boolean; }> => {
    const apiCall = httpsCallable<{ publicId: string, password?: string | null }, { exists: boolean; error: string | null; banana: any; bananaOperations: any[]; isWrapped: boolean; role: UserRole; canBananaBack: boolean; }>(
      getFunctions(app),
      "getBananaByPublicId"
    );
    try {
      const res = await apiCall({ publicId, password });
      return res.data;
    } catch (error: any) {
      throw new Error(error.message || "Failed to fetch banana by public ID.");
    }
  }

  public static getBananaByPrivateId = async (privateId: string, password?: string | null): Promise<{ exists: boolean; error: string | null; banana: any; bananaOperations: any[]; isWrapped: boolean; role: UserRole; canBananaBack: boolean; }> => {
    const apiCall = httpsCallable<{ privateId: string, password?: string | null }, { exists: boolean; error: string | null; banana: any; bananaOperations: any[]; isWrapped: boolean; role: UserRole; canBananaBack: boolean; }>(
      getFunctions(app),
      "getBananaByPrivateId"
    );
    try {
      const res = await apiCall({ privateId, password });
      return res.data;
    } catch (error: any) {
      throw new Error(error.message || "Failed to fetch banana by private ID.");
    }
  }
}
